/**
 * What `GET /auth/session` says, read out of an unknown body.
 *
 * The signed-in shell needs these at boot -- who the browser is, which
 * projects it may read, what the deployment's account service does -- and
 * nothing of the account pages' markup. They live apart from `public-auth.ts`
 * so the entry script reads a session without carrying the sign-in forms.
 */

/**
 * The account the Worker says this browser is signed in as.
 *
 * Mirrors `PublicAccount` in matrixbased's `src/auth.ts`; the shape is the
 * response body of `GET /auth/session`, which is a wire contract rather than
 * an import the browser bundle can make.
 */
export type SignedInAccount = {
  id: string;
  email: string;
  name: string;
  email_verified: boolean;
};

/** Reads one out of an unknown body, or nothing if the body is not one. */
export function signedInAccount(body: unknown): SignedInAccount | null {
  if (body === null || typeof body !== 'object') return null;
  const account = (body as { account?: unknown }).account;
  if (account === null || typeof account !== 'object') return null;
  const row = account as Record<string, unknown>;
  if (typeof row['id'] !== 'string' || typeof row['email'] !== 'string') return null;
  return {
    id: row['id'],
    email: row['email'],
    name: typeof row['name'] === 'string' ? row['name'] : row['email'],
    email_verified: row['email_verified'] === true,
  };
}

/**
 * What this deployment's account service does, as `/auth/session` reports it.
 * Development opens sign-up and hands links back on the page; a deployment is
 * the invite-only beta, mails its links when it has a transport, and gates the
 * invite request and sign-up with the Turnstile widget it names.
 */
export type SiteAccounts = {
  signup: 'open' | 'invite';
  mail: boolean;
  /** The widget the gated forms render; null when the Worker names none. */
  turnstileSiteKey: string | null;
};

/**
 * Assumed until the Worker answers: nothing it has not confirmed. No emailed
 * link is offered and no widget drawn on a promise the deployment may not keep.
 */
export const BETA_SITE_ACCOUNTS: SiteAccounts = { signup: 'invite', mail: false, turnstileSiteKey: null };

/** Reads the deployment's account service out of a `/auth/session` body. */
export function siteAccounts(body: unknown): SiteAccounts | null {
  if (body === null || typeof body !== 'object' || !('signup' in body) || !('mail' in body)) return null;
  const { signup, mail } = body;
  if ((signup !== 'open' && signup !== 'invite') || typeof mail !== 'boolean') return null;
  // A Worker from before the challenge omits the key; that is a deployment without one.
  const key = 'turnstile_site_key' in body ? body.turnstile_site_key : null;
  return { signup, mail, turnstileSiteKey: typeof key === 'string' && key.length > 0 ? key : null };
}

/**
 * One project the signed-in account may read, as `/auth/session` lists it.
 * `scope` is the `owner/repo` the `/v1/` reads take as `?project=`; mirrors
 * `Membership` in matrixbased's `src/tenancy.ts`.
 */
export type ProjectMembership = { scope: string; name: string; role: string | null };

/**
 * The account's projects out of a `/auth/session` body.
 *
 * Null when the body lists none at all: a Worker without tenancy, or an
 * authority served without the site in front of it, which serves the one
 * project its snapshot names. An empty list is different: an account that is
 * a member of nothing, which every `/v1/` read would refuse `no_project`.
 * A malformed row is dropped rather than guessed at.
 */
export function projectMemberships(body: unknown): ProjectMembership[] | null {
  if (body === null || typeof body !== 'object' || !('projects' in body) || !Array.isArray(body.projects)) {
    return null;
  }
  const rows: unknown[] = body.projects;
  return rows.flatMap((row): ProjectMembership[] => {
    if (row === null || typeof row !== 'object' || !('scope' in row)) return [];
    const { scope } = row;
    if (typeof scope !== 'string' || scope.length === 0) return [];
    const name = 'name' in row && typeof row.name === 'string' && row.name.length > 0 ? row.name : scope;
    const role = 'role' in row && typeof row.role === 'string' ? row.role : null;
    return [{ scope, name, role }];
  });
}
